// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { AppIcon } from '@shared/ui-kit/components/app-icon';
import { ListSuggestions } from '@shared/ui-kit/components/autocomplete';
import {
  type AllBenchmarks,
  formattedBenchmarks,
  selectPopularThematics,
  selectPopularUsIndexes,
} from '@advisor-app/features/utils/benchmark-formatter';
import { type SearchUniverses } from '@shared/data-access/interfaces/api/ai/screener';
import { useSelectUniverse } from '@advisor-app/hooks';
import { useState } from 'react';
import { useAppSelector } from '@advisor-app/store/store';
import { selectBenchmark } from '@advisor-app/store/slices/parameters';
import { useKeyboardNavigation, useSearchUniverse } from '../hooks';
import { useTranslation } from 'react-i18next';

type SearchUniverseProps = {
  readonly assetType: string;
};

function differenceBy<T>(array: T[], filterArray: T[], property: keyof T): T[] {
  const filterSet = new Set(filterArray.map(item => item[property]));
  return array.filter(item => !filterSet.has(item[property]));
}

export const SearchUniverse = ({ assetType }: SearchUniverseProps) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  const [benchmarkSelected] = useAppSelector(state =>
    selectBenchmark(state, 'ai-builder'),
  );

  const popularIndexes = useAppSelector(selectPopularUsIndexes);
  const popularThematics = useAppSelector(selectPopularThematics);

  const universesOnScreen = [...popularIndexes, ...popularThematics];

  const { handleSelectBenchmark } = useSelectUniverse('ai-builder');

  const [renderSuggestions, setRenderSuggestions] = useState(false);

  const { isLoading, universes } = useSearchUniverse(searchText);

  const diffUniverses = differenceBy(universes, universesOnScreen, 'ticker');
  const formattedDiffUniverses = diffUniverses.map(({ description, ...rest }) => ({
    name: description,
    ...rest,
  }));

  const handleSelectValue = (item: SearchUniverses | string) => {
    handleSelectBenchmark(typeof item === 'string' ? item : item.ticker);
    setRenderSuggestions(false);
    setSearchText('');
  };

  const { suggestionIndex, handleKeyDown, resetSuggestionIndex } =
    useKeyboardNavigation({ handleSelectValue, items: universes });

  const handleOverList = () => {
    resetSuggestionIndex();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    setRenderSuggestions(true);
    resetSuggestionIndex();
  };

  return (
    <div className="search-universe-container flex items-center space-x-8">
      <div>
        <div className="relative w-[220px]" data-testid="search-universe">
          <input
            className="input-rounded pl-10"
            value={searchText}
            onFocus={() => setRenderSuggestions(true)}
            onChange={handleInputChange}
            onBlur={() => setRenderSuggestions(false)}
            onKeyDown={handleKeyDown}
            name="search_text"
            placeholder={t('ai-builder.search', { ns: 'ai-builder' }) as string}
            required
            type="text"
            autoComplete="off"
          />
          <div className="absolute left-4 top-3">
            <AppIcon icon="Search" color="disabled" size="base" />
          </div>

          <ListSuggestions
            allTickers={searchText.length === 0 ? null : formattedDiffUniverses}
            handleSelectValue={handleSelectValue}
            isLoading={isLoading}
            renderSuggestions={renderSuggestions}
            suggestionIndex={suggestionIndex}
            handleOverList={handleOverList}
            typeOfAssetSearch="universe"
          />
        </div>
      </div>

      <h4 className="text-xl font-bold">
        {!!benchmarkSelected &&
          `${
            assetType === 'stock'
              ? t('ai-builder.universe', { ns: 'ai-builder' })
              : t('ai-builder.benchmark', { ns: 'ai-builder' })
          } ${t('ai-builder.selected', { ns: 'ai-builder' })}: ${
            formattedBenchmarks[benchmarkSelected.ticker as AllBenchmarks]
          }`}
      </h4>
    </div>
  );
};
